<template>
  <div :class="$style.container">
    <div :class="$style.tabs">
      <div :class="$style.scrollPane">
        <a
          v-for="(tab, index) in config"
          :key="`tab-${index}`"
          :class="[
            $style.tab,
            { [$style.active]: activeTab === index + 1 },
            { [$style.disabled]: tab.disabled },
          ]"
          @click="(e) => updateTabs(e, index + 1)"
          v-html="tab.title"
        />
      </div>
    </div>
    <div
      v-for="(tab, index) in config"
      :key="`content-${index}`"
      :class="[$style.content, { [$style.activeContent]: activeTab === index + 1 }]"
    >
      <div v-if="!contentLoaded" class="preloadFilter"><Loading /></div>

      <template v-if="activeTab === index + 1">
        <SidebarSlider v-if="tab.sliders?.length" :slides="tab.sliders" />
        <FAQList v-else-if="tab.FAQListConfig" :config="tab.FAQListConfig" />
        <div v-else v-html="content" />
      </template>
    </div>
  </div>
</template>

<script setup>
import SidebarSlider from '../SidebarSlider/SidebarSlider.vue';
import FAQList from '../FAQList/FAQList.vue';
import Loading from '../Loading/Loading.vue';
import { onMounted, onUnmounted, ref, nextTick } from 'vue';
import { checkIsMobile } from '../../utils/media';
import safeFetch from '../../utils/safeFetch';
import initModal from '../../build-components/Modal/main.js';
import { getVendorConsent } from '../../globals/consentmanager';
import cmpVendorIDs from '../../globals/constants/cmpVendorIDs';

const props = defineProps({
  config: {
    type: Array,
    default: () => [],
  },
});

const isMobile = ref(false);
const activeTab = ref(null);
const mobileHidden = ref(null);
const content = ref(null);
const contentLoaded = ref(false);
const activeTabPreselected = ref(false);
const html = ref(null);

const checkScreenSize = () => (isMobile.value = checkIsMobile());

const getContentFromId = (id) => {
  html.value = document.getElementById(id);

  if (html.value) {
    content.value = html.value.innerHTML;
    contentLoaded.value = true;
    nextTick(() => {
      initModal();
      bindVideoModals();
    });

    if (window.initGoogleMapsInContentTabs) {
      window.requestAnimationFrame(window.initGoogleMapsInContentTabs);
    }

    if (window.initFlipCardInContentTabs) {
      window.requestAnimationFrame(window.initFlipCardInContentTabs);
    }

    if (window.initRotatingCarouselInContentTabs) {
      window.requestAnimationFrame(window.initRotatingCarouselInContentTabs);
    }
  } else {
    content.value = null;
    contentLoaded.value = false;
  }
};

const getContentFromUrl = (url) => {
  safeFetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then((text) => {
      content.value = text;
      contentLoaded.value = true;
      nextTick(() => {
        initModal();
        bindVideoModals();
      });
    })
    .catch((error) => {
      console.log(error);
      content.value = null;
      contentLoaded.value = false;
    });
};

const updateTabs = (event, tab) => {
  const elm = event.currentTarget;
  if (!elm.classList.contains('ContentTabs__disabled')) {
    contentLoaded.value = false;
    if (props.config[tab - 1].url) {
      getContentFromUrl(props.config[tab - 1].url);
      content.value = '';
    } else if (props.config[tab - 1].sliders) {
      content.value = null;
      contentLoaded.value = true;
    } else if (props.config[tab - 1].id) {
      getContentFromId(props.config[tab - 1].id);
    } else {
      content.value = null;
    }
    if (props.config[tab - 1].FAQListConfig) {
      contentLoaded.value = true;
    }
    if (isMobile.value && activeTab.value === tab && activeTab.value !== mobileHidden.value) {
      mobileHidden.value = tab;
    } else if (
      isMobile.value &&
      activeTab.value === tab &&
      activeTab.value === mobileHidden.value
    ) {
      mobileHidden.value = null;
    } else if (isMobile.value && activeTab.value !== tab) {
      mobileHidden.value = null;
      activeTab.value = tab;
    } else {
      activeTab.value = tab;
    }
  }
};

const bindVideoModals = () => {
  setTimeout(() => {
    document.querySelectorAll('.ContentTeaserS__container').forEach((figure) => {
      const videoLink = figure.querySelector('.ContentTeaserS__videoLink');
      const modal = figure.querySelector('.modal');
      const iframe = figure.querySelector('iframe');
      const consentBox = figure.querySelector('.Modal__consent');

      if (consentBox) {
        consentBox.querySelector('.Button__close')?.classList.remove('Button__close');

        const agreeBtn = consentBox.querySelector('.Modal__consentButton, button');
        if (agreeBtn && !agreeBtn.dataset.bound) {
          agreeBtn.dataset.bound = 'true';
          agreeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const activateVideo = () => {
              if (iframe) {
                const dataSrc = iframe.getAttribute('data-src');
                if (dataSrc) iframe.setAttribute('src', dataSrc);
                iframe.style.display = '';
              }
              consentBox.style.display = 'none';
            };

            if (window.cmpConsentType === 'consentmanager' && window.__cmp) {
              window.__cmp('setVendorConsent', [cmpVendorIDs.YOUTUBE.id, 1], activateVideo);
            }
          });
        }
      }

      if (videoLink && modal && iframe && !videoLink.dataset.bound) {
        videoLink.dataset.bound = 'true';
        videoLink.addEventListener('click', (e) => {
          e.preventDefault();

          let hasConsent = false;
          try {
            hasConsent = getVendorConsent(cmpVendorIDs.YOUTUBE.id);
          } catch {
            // ignore error
          }

          if (hasConsent) {
            const dataSrc = iframe.getAttribute('data-src');
            if (!iframe.getAttribute('src') && dataSrc) iframe.setAttribute('src', dataSrc);

            iframe.style.display = '';
            if (consentBox) consentBox.style.display = 'none';
          } else {
            iframe.style.display = 'none';
            if (consentBox) consentBox.style.display = '';
          }

          modal.classList.add('is-active');
        });
      }
    });
  }, 500);
};

onMounted(() => {
  bindVideoModals();
  setTimeout(() => {
    document.addEventListener('click', (e) => {
      if (
        e.target.matches('.modal-close') ||
        e.target.closest('.modal-close') ||
        e.target.matches('.modal-background')
      ) {
        const activeModal = document.querySelector('.modal.is-active');
        if (activeModal) {
          const iframe = activeModal.querySelector('iframe');
          if (iframe) {
            iframe.setAttribute('src', '');
          }
          activeModal.classList.remove('is-active');
        }
      }
    });
  }, 500);
  window.addEventListener('resize', checkScreenSize);
  checkScreenSize();
  const queries = window.location.search.substring(1).split('&');
  let queryStringTabId = null;
  if (queries) {
    for (const query of queries) {
      if (query.includes('tab')) {
        queryStringTabId = query.split('=')[1];
      }
    }
  }

  for (const [index, tab] of props.config.entries()) {
    if (
      (queryStringTabId && queryStringTabId == index + 1 && !tab.disabled) ||
      (tab.active && !tab.disabled)
    ) {
      activeTab.value = index + 1;
      activeTabPreselected.value = true;
      if (tab.url) {
        getContentFromUrl(tab.url);
      } else if (tab.sliders) {
        content.value = null;
        contentLoaded.value = true;
      } else if (tab.id) {
        getContentFromId(tab.id);
      }
    }
  }
  if (!activeTabPreselected.value) {
    activeTab.value = 1;
    if (props.config[0].url) {
      getContentFromUrl(props.config[0].url);
    } else if (props.config[0].sliders) {
      content.value = null;
      contentLoaded.value = true;
    } else if (props.config[0].id) {
      getContentFromId(props.config[0].id);
    }
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', checkScreenSize);
});
</script>

<style module lang="scss">
@forward './ContentTabs.scss';
</style>
